import React, { useState, createContext, useContext, useEffect } from 'react';
import { FETCH_RX_PRODUCT_URL, RxProduct } from '../site/types'; // Assuming you have your API URL here
import { HandleAxios } from '@shared_components/components/requests/HandleAxios'; // Adjust the import path as needed
// Define the context type
interface ProductContextType {
  product: RxProduct | null;
  loading: boolean;
  error: string | null;
  refetchProduct: () => void;
}

// Create the context with a default value
const ProductContext = createContext<ProductContextType>({
  product: null,
  loading: false,
  error: null,
  refetchProduct: () => {}, // No-op function as default
});

// Create the provider component
interface ProductProviderProps {
  children: React.ReactNode;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const [product, setProduct] = useState<RxProduct | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProduct = () => {
    setLoading(true);
    setError(null); // Reset error on refetch

    const handleComplete = (response: any) => {
      setLoading(false);
      setProduct(response.data);
    };

    const handleError = (error: any) => {
      setLoading(false);
      setError(error.message);
      console.error("Error loading product:", error);
      // You might want to handle textData update here or in the consuming component
      // setTextData({ text: "Error loading product", variant: "danger" });
    };

    HandleAxios({
      url: FETCH_RX_PRODUCT_URL,
      method: "get",
      data: null,
      onComplete: handleComplete,
      onError: handleError,
    });
  };

  useEffect(() => {
    fetchProduct();
    // You might want to add dependencies here if the URL or other fetch parameters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchProduct = () => {
    fetchProduct();
  };

  // Provide the context value to the children
  return (
    <ProductContext.Provider value={{ product, loading, error, refetchProduct }}>
      {children}
    </ProductContext.Provider>
  );
};

// Create a custom hook to easily use the product context
export const useProduct = () => {
  return useContext(ProductContext);
};